import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { Shell } from './components/Shell.js';
import { TodayScreen } from './screens/Today.js';
import { StopScreen } from './screens/Stop.js';
import { PendingScreen } from './screens/Pending.js';
import { ProspectScreen } from './screens/Prospect.js';
import { SettingsScreen } from './screens/Settings.js';

/**
 * Routing.
 *
 * Five routes, in French, because the URL is read by whoever is debugging a phone over the
 * telephone and *arret* is what both of them will say.
 *
 * The same rule as the portal applies and matters more here: **anything the user would be
 * annoyed to lose on a reload lives in the URL.** A field phone gets killed by the operating
 * system whenever the camera needs the memory, and a technician who loses which panel they
 * were on every time they photograph something stops photographing things.
 */

const rootRoute = createRootRoute({
  component: () => (
    <Shell>
      <Outlet />
    </Shell>
  ),
});

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: z.object({
    /** `YYYY-MM-DD`. Absent means today, so the bare URL is always current. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  component: TodayScreen,
});

/** The same screen, reachable by an explicit day — the technician checking tomorrow. */
const dayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jour',
  validateSearch: z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional() }),
  component: TodayScreen,
});

const stopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/arret/$stopId',
  validateSearch: z.object({
    /** Which panel is open. In the URL so the camera killing the app costs nothing. */
    panel: z.enum(['door', 'work', 'finish', 'cannot']).optional(),
  }),
  component: StopScreen,
});

const pendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/envois',
  component: PendingScreen,
});

const prospectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/observation',
  component: ProspectScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appareil',
  component: SettingsScreen,
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  dayRoute,
  stopRoute,
  pendingRoute,
  prospectRoute,
  settingsRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
